/* eslint-disable @typescript-eslint/no-explicit-any */
import { api_service } from '../api/api-service';
import { api_urls } from '../api/api-urls';
import { ResendOtpRequest } from '../pages/registration/registration-types';

export interface LoginRequest {
    username?: string;
    password?: string;
    email?: string;
    phoneNumber?: string;
    contractId?: string;
    ssnNumber?: string;
    postCode?: string;
    fedTaxNumber?: string;
    dateOfBirth?: string;
    portalId?: number | string | null;
    [key: string]: any;
}

export interface FindUserEmailByContractIdRequest {
    contractId?: string;
    fedTaxNumber?: string;
    postCode?: string;
    dateOfBirth?: string;
    ssnNumber?: string;
    [key: string]: any;
}

export interface ForgotPasswordRequest {
    email?: string;
    [key: string]: any;
}
export interface LoginResponse {
    statusCode?: number;
    data?: any;
    message?: string;
    accessToken?: string;
    refreshToken?: string;
    [key: string]: any;
}
export interface ApiResponse {
    success: boolean;
    message: string;
    data: {
        [key: string]: any;
    };
}

/**
 * Login service to authenticate user
 * @param credentials - Login credentials (username, password, etc.)
 * @param showLoader - Whether to show loading indicator (default: true)
 * @returns Promise with login response
 */
export const userLogin = async (credentials: LoginRequest, showLoader: boolean = true): Promise<LoginResponse> => {
    const response: any = await api_service.post({
        endPoint: api_urls.Login,
        payLoad: credentials,
        showLoader,
    });
    return response?.data || response;
};
export const getLoginUserData = async (): Promise<ApiResponse> => {
    try {
        const response: any = await api_service.get({
            endPoint: '/login/user', // replace with your actual endpoint
            showLoader: true,
        });

        return response as ApiResponse;
    } catch (error: any) {
        console.error('Error fetching login user data:', error);

        return {
            success: false,
            message: error.message || 'Something went wrong',
            data: {},
        };
    }
};

// validate contract id and get contract details
export const validateContractId = async (contractId: string, showLoader: boolean = true): Promise<ApiResponse> => {
    try {
        const response: any = await api_service.post({
            payLoad: { contractId: contractId },
            endPoint: api_urls.ValidateContractId,
            showLoader,
        });
        return response as ApiResponse;
    } catch (error: any) {
        console.error('Error validating contract id:', error);
        throw error;
    }
};

// api to find user email by contract id
export const findUserEmailByContractId = async (
    inputPayload: FindUserEmailByContractIdRequest,
    showLoader: boolean = true,
): Promise<ApiResponse> => {
    try {
        const response: any = await api_service.post({
            endPoint: api_urls.FindUserEmailByContractId,
            showLoader,
            payLoad: inputPayload,
        });
        return response as ApiResponse;
    } catch (error: any) {
        console.error('Error finding user email by contract id:', error);
        throw error;
    }
};

// forgot password
export const forgotLoginPassword = async (
    inputPayload: ForgotPasswordRequest,
    showLoader: boolean = true,
): Promise<ApiResponse> => {
    try {
        const response: any = await api_service.post({
            endPoint: api_urls.ForgotPassword,
            showLoader,
            payLoad: inputPayload,
        });
        return response?.data as ApiResponse;
    } catch (error: any) {
        console.error('Error forgot password:', error);
        throw error;
    }
};

export interface SendLoginOtpRequest extends ResendOtpRequest {
    otpChannel?: 'email' | 'sms';
}

// api to send the first login passcode on the channel chosen on the two-factor screen
export const sendLoginOtp = async (
    inputPayload: SendLoginOtpRequest,
    showLoader: boolean = true,
): Promise<ApiResponse> => {
    try {
        const response: any = await api_service.post({
            endPoint: api_urls.SendLoginOtp,
            showLoader,
            payLoad: inputPayload,
        });
        return response?.data as ApiResponse;
    } catch (error: any) {
        console.error('Error sending login Passcode:', error);
        throw error;
    }
};

// api to send a passcode that verifies an existing unverified primary mobile number
export const sendSelfVerifyPhoneOtp = async (
    inputPayload: { userId: string; phoneNumber: string; portalId: number },
    showLoader: boolean = true,
): Promise<ApiResponse> => {
    try {
        const response: any = await api_service.post({
            endPoint: api_urls.SelfVerifyPhoneSendOtp,
            showLoader,
            payLoad: inputPayload,
        });
        return response?.data as ApiResponse;
    } catch (error: any) {
        console.error('Error sending mobile verification Passcode:', error);
        throw error;
    }
};

// api to confirm the mobile verification passcode
export const confirmSelfVerifyPhoneOtp = async (
    inputPayload: { userId: string; phoneNumber: string; otp: number; portalId: number },
    showLoader: boolean = true,
): Promise<ApiResponse> => {
    try {
        const response: any = await api_service.post({
            endPoint: api_urls.SelfVerifyPhoneConfirm,
            showLoader,
            payLoad: inputPayload,
        });
        return response?.data as ApiResponse;
    } catch (error: any) {
        console.error('Error confirming mobile verification Passcode:', error);
        throw error;
    }
};

// api to resend login passcode
export const resendLoginOtp = async (
    inputPayload: ResendOtpRequest,
    showLoader: boolean = true,
): Promise<ApiResponse> => {
    try {
        const response: any = await api_service.post({
            endPoint: api_urls.ResendLoginOtp,
            showLoader,
            payLoad: inputPayload,
        });
        return response?.data as ApiResponse;
    } catch (error: any) {
        console.error('Error resending login Passcode:', error);
        throw error;
    }
};

// api to verify login passcode
export const verifyLoginOtp = async (inputPayload: any, showLoader: boolean = true): Promise<ApiResponse> => {
    try {
        const response: any = await api_service.post({
            endPoint: api_urls.VerifyLoginOtp,
            showLoader,
            payLoad: { ...inputPayload, clientId: 1 },
        });
        return response.data as ApiResponse;
    } catch (error: any) {
        console.error('Error verifying login Passcode:', error);
        throw error;
    }
};

// api to verify forgot password passcode
export const verifyForgotPasswordOtp = async (inputPayload: any, showLoader: boolean = true): Promise<ApiResponse> => {
    try {
        const response: any = await api_service.post({
            endPoint: api_urls.VerifyForgotPasswordOtp,
            showLoader,
            payLoad: inputPayload,
        });
        return response.data as ApiResponse;
    } catch (error: any) {
        console.error('Error verifying forgot password Passcode:', error);
        throw error;
    }
};

// api to reset forgot password
export const resetForgotPassword = async (inputPayload: any, showLoader: boolean = true): Promise<ApiResponse> => {
    try {
        const response: any = await api_service.post({
            endPoint: api_urls.ResetForgotPassword,
            showLoader,
            payLoad: inputPayload,
        });
        return response.data as ApiResponse;
    } catch (error: any) {
        console.error('Error resetting forgot password:', error);
        throw error;
    }
};

// api to refresh token
export const refreshToken = async (refreshToken: string, showLoader: boolean = true): Promise<LoginResponse> => {
    try {
        const response: any = await api_service.post({
            endPoint: api_urls.RefreshToken,
            showLoader,
            customHeaders: {
                Authorization: refreshToken,
            },
        });
        return response?.data || response;
    } catch (error: any) {
        console.error('Error refreshing token:', error);
        throw error;
    }
};

// api to get user notifications count
export const getUserNotificationsCount = async (showLoader: boolean = true): Promise<ApiResponse> => {
    try {
        const response: any = await api_service.get({
            endPoint: api_urls.GetUserNotificationsCount,
            showLoader,
        });
        return response?.data as ApiResponse;
    } catch (error: any) {
        console.error('Error getting user notifications count:', error);
        throw error;
    }
};

export const getAllNotifications = async (inputPayload: any, showLoader: boolean = true): Promise<ApiResponse> => {
    try {
        const response: any = await api_service.get({
            endPoint: api_urls.GetAllNotifications,
            payLoad: inputPayload,
            showLoader,
        });
        return response?.data as ApiResponse;
    } catch (error: any) {
        console.error('Error getting all notifications:', error);
        throw error;
    }
};

// api to start the update-phone-number recovery flow (sends a passcode to the account email)
export const initiateUpdatePhone = async (
    inputPayload: { email: string; contractId: string; portalId: number },
    showLoader: boolean = true,
): Promise<ApiResponse> => {
    try {
        const response: any = await api_service.post({
            endPoint: api_urls.UpdatePhoneInitiate,
            showLoader,
            payLoad: inputPayload,
        });
        return response?.data as ApiResponse;
    } catch (error: any) {
        console.error('Error starting phone number update:', error);
        throw error;
    }
};

// api to verify the email passcode; the response carries a short-lived token for the add-phone calls
export const verifyUpdatePhoneOtp = async (
    inputPayload: { email: string; otp: number; portalId: number },
    showLoader: boolean = true,
): Promise<ApiResponse> => {
    try {
        const response: any = await api_service.post({
            endPoint: api_urls.UpdatePhoneVerifyOtp,
            showLoader,
            payLoad: inputPayload,
        });
        return response?.data as ApiResponse;
    } catch (error: any) {
        console.error('Error verifying phone number update Passcode:', error);
        throw error;
    }
};
